import { Box, Button, Paper, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { InlineAlert, LoadingButton } from "src/grading/components";
import { formatScore as fmt } from "src/grading/components/EditScoreDrawer/EditScoreDrawer";
import FlagCard, {
  FlagCardAction,
} from "src/grading/components/FlagCard/FlagCard";
import { modificationLabel } from "src/grading/utils/accommodationLabels";
import type { ApiError } from "src/models";
import {
  EVENT_LABEL,
  EventId,
  FlagType,
  ReviewFlag,
  SessionDetail,
  StudentResult,
} from "src/models/sessions";
import {
  useRecomputeFlagsMutation,
  useResolveFlagMutation,
} from "src/store/reducers/sessions/sessionsApiSlice";

const LABEL_KEY: Record<FlagType, string> = {
  missing_category: "review.missingCategory",
  mod_without_note: "review.modWithoutNote",
  missing_mode: "review.missingMode",
  outlier: "review.outlier",
  duplicate: "review.duplicate",
  camera_disagreement: "review.cameraDisagreement",
  conflict: "review.conflict",
  age_below_range: "review.ageBelowRange",
  superseded_certificate: "review.supersededCertificate",
  join_abuse: "review.joinAbuse",
};
const ORDER: FlagType[] = [
  "duplicate",
  "conflict",
  "missing_category",
  "outlier",
  "camera_disagreement",
  "mod_without_note",
  "missing_mode",
  "age_below_range",
  "superseded_certificate",
  "join_abuse",
];
interface Props {
  session: SessionDetail;
  students: StudentResult[];
  flags: ReviewFlag[];
  locked: boolean;
  onEditEntry?: (studentId: string, entryId: string) => void;
}

const WARN: FlagType[] = [
  "duplicate",
  "conflict",
  "missing_category",
  "outlier",
  "camera_disagreement",
];

/** Open review flags as cards, each with the resolution it needs (FR-025). */
const FlagsPanel = ({
  session,
  students,
  flags,
  locked,
  onEditEntry,
}: Props) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [resolveFlag, { isLoading: resolving }] = useResolveFlagMutation();
  const [recompute, { isLoading: recomputing }] = useRecomputeFlagsMutation();
  const [showResolved, setShowResolved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameOf = (studentId: string | null) => {
    const s = students.find((x) => x.studentId === studentId);
    return s ? `${s.lastName}, ${s.firstName}` : "Session";
  };
  const entryOf = (entryId: unknown) =>
    session.entries.find((e) => e.id === entryId);
  const open = useMemo(() => {
    const items = flags.filter((f) => f.status === "open");
    return ORDER.flatMap((type) => items.filter((f) => f.type === type));
  }, [flags]);
  const resolved = flags.filter((f) => f.status !== "open");
  const duplicates = open.filter((f) => f.type === "duplicate").length;

  const act = async (
    flag: ReviewFlag,
    status: "resolved" | "dismissed",
    keepEntryId?: string,
  ) => {
    if (resolving) return;
    setError(null);
    try {
      await resolveFlag({
        sessionId: session.id,
        flagId: flag.id,
        status,
        keepEntryId,
      }).unwrap();
    } catch (e) {
      const message = (e as ApiError | undefined)?.data?.message;
      setError(
        Array.isArray(message)
          ? message.join(", ")
          : (message ??
              t("review.flagUpdateError", {
                defaultValue: "Could not update the flag.",
              })),
      );
    }
  };

  const describe = (f: ReviewFlag) => {
    const d = f.details ?? {};
    const ev = t(`events.${d.eventId}`, {
      defaultValue: EVENT_LABEL[d.eventId as EventId] ?? d.eventId,
    });
    switch (f.type) {
      case "missing_category":
        return t("review.flag.missingCategory", {
          defaultValue:
            "No {{category}} entry yet. Score the event, or mark the student absent or excused.",
          category: t(`categories.${d.category}`),
        });
      case "outlier":
        return t("review.flag.outlier", {
          defaultValue:
            "{{event}}: {{value}} is outside the plausible range {{min}} to {{max}} for this age and benchmark table. Confirm the value or correct the entry.",
          event: ev,
          value: fmt(d.eventId, d.value),
          min: fmt(d.eventId, d.plausibleMin),
          max: fmt(d.eventId, d.plausibleMax),
        });
      case "duplicate":
        return t("review.flag.duplicate", {
          defaultValue:
            "{{event}}: two scorers recorded this event. Kept {{kept}}; the other entry was {{other}}. Choose which one to keep.",
          event: ev,
          kept: fmt(d.eventId, d.keptValue),
          other: fmt(d.eventId, d.supersededValue),
        });
      case "conflict":
        return t("review.flag.conflict", {
          defaultValue:
            "{{event}}: a scorer sent {{sent}} after you edited this entry to {{kept}}. Your value was kept.",
          event: ev,
          sent: fmt(d.eventId, d.rejectedValue),
          kept: fmt(d.eventId, d.keptValue),
        });
      case "camera_disagreement":
        return t("review.flag.cameraDisagreement", {
          defaultValue:
            "{{event}}: the human count {{human}} and the camera count {{camera}} differ by more than {{gap}}. The human count is recorded; confirm or correct it.",
          event: ev,
          human: d.humanCount,
          camera: d.cameraCount,
          gap: d.gap,
        });
      case "mod_without_note":
        return t("review.flag.modWithoutNote", {
          defaultValue:
            "{{event}}: recorded as {{modification}} without a note. Add the note.",
          event: ev,
          modification: modificationLabel(t, String(d.modification)),
        });
      case "missing_mode":
        return t("review.flag.missingMode", {
          defaultValue: "{{event}}: set the mode ({{modes}}).",
          event: ev,
          modes: (d.modes ?? []).join(", "),
        });
      case "age_below_range":
        return t("review.flag.ageBelowRange", {
          defaultValue:
            "Age {{age}}: the official table starts at 6, so the age-6 row is used.",
          age: d.ageAtTest,
        });
      case "superseded_certificate":
        return t("review.flag.supersededCertificate", {
          defaultValue:
            "Award changed from {{from}} to {{to}} after reopening. Reprint the certificate; the earlier one is superseded.",
          from: t(`results.tier.${d.from ?? "incomplete"}`),
          to: t(`results.tier.${d.to}`),
        });
      case "join_abuse":
        return t("review.flag.joinAbuse", {
          defaultValue:
            "Join code {{code}} saw {{attempts}} attempts in a minute. Regenerate the code if it leaked.",
          code: d.code,
          attempts: d.attempts,
        });
      default:
        return JSON.stringify(d);
    }
  };

  /** Every action the flag offers, most decisive first. */
  const actionsOf = (f: ReviewFlag): FlagCardAction[] => {
    const entry = entryOf(f.details?.keptEntryId ?? f.entryId);
    const pair = f.type === "duplicate" || f.type === "conflict";
    const list: FlagCardAction[] = [];
    if (pair) {
      list.push(
        {
          label: `${t("review.keepThis")} ${fmt(f.details.eventId, f.details.keptValue)}`,
          onClick: () => act(f, "resolved", f.details.keptEntryId),
        },
        {
          label: `${t("review.keepThis")} ${fmt(f.details.eventId, f.details.supersededValue ?? f.details.rejectedValue)}`,
          onClick: () =>
            act(
              f,
              "resolved",
              f.details.supersededEntryId ?? f.details.rejectedEntryId,
            ),
        },
      );
    }
    if (entry && onEditEntry && !pair) {
      list.push({
        label: t("review.editEntry", { defaultValue: "Edit entry" }),
        onClick: () => onEditEntry(f.studentId ?? entry.studentId, entry.id),
      });
    }
    if (!pair) {
      list.push({
        label: t("review.resolve"),
        onClick: () => act(f, "resolved"),
      });
    }
    if (
      entry &&
      (f.type === "camera_disagreement" || entry.recordedByType === "self-test")
    ) {
      list.push({
        label: t("review.reviewVideo", { defaultValue: "Review video" }),
        onClick: () =>
          history.push(
            `/grading/sessions/${session.id}/entries/${entry.id}/video`,
          ),
      });
    }
    list.push({
      label: t("review.dismiss"),
      onClick: () => act(f, "dismissed"),
    });
    return list;
  };

  const titleOf = (f: ReviewFlag) =>
    `${t(LABEL_KEY[f.type])} · ${nameOf(f.studentId)}`;

  return (
    <Box component="section" aria-labelledby="review-flags">
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 1.5,
        }}
      >
        <Typography id="review-flags" variant="h6" component="h2">
          {t("review.flags")} ·{" "}
          {open.length
            ? t("review.openCount", {
                defaultValue: "{{count}} open",
                count: open.length,
              })
            : t("review.noneOpen", { defaultValue: "none open" })}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button size="small" onClick={() => setShowResolved((v) => !v)}>
            {showResolved
              ? t("review.hideResolved", { defaultValue: "Hide resolved" })
              : t("review.showResolved", {
                  defaultValue: "Show resolved ({{count}})",
                  count: resolved.length,
                })}
          </Button>
          {!locked && (
            <LoadingButton
              size="small"
              variant="outlined"
              loading={recomputing}
              onClick={() => recompute(session.id)}
              label={t("review.recheck")}
            />
          )}
        </Box>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {duplicates > 0 && !locked ? (
          <InlineAlert severity="warning">
            {t("review.duplicatesBlock", {
              defaultValue:
                "Finalize is blocked until every duplicate is resolved.",
            })}
          </InlineAlert>
        ) : null}
        {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}
        {open.length === 0 ? (
          <Typography color="text.secondary">
            {t("review.allClear", {
              defaultValue:
                "No open flags. Entries, modes, notes and plausible ranges all check out.",
            })}
          </Typography>
        ) : (
          <Box
            sx={{
              display: "grid",
              gap: 1.5,
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            }}
          >
            {open.map((f) => {
              const tone = WARN.includes(f.type) ? "warn" : "neutral";
              if (locked) {
                return (
                  <Paper
                    key={f.id}
                    elevation={0}
                    role="group"
                    aria-label={titleOf(f)}
                    sx={{ p: 2, bgcolor: "surfaceAlt" }}
                  >
                    <Typography
                      component="h3"
                      sx={{ fontSize: 14, fontWeight: 800 }}
                    >
                      {titleOf(f)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {describe(f)}
                    </Typography>
                  </Paper>
                );
              }
              const [primary, secondary, ...rest] = actionsOf(f);
              return (
                <Box
                  key={f.id}
                  role="group"
                  aria-label={titleOf(f)}
                  data-flag={f.type}
                >
                  <FlagCard
                    title={titleOf(f)}
                    body={describe(f)}
                    primary={primary}
                    secondary={secondary}
                    tone={tone}
                  />
                  {rest.length > 0 ? (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {rest.map((a) => (
                        <Button key={a.label} size="small" onClick={a.onClick}>
                          {a.label}
                        </Button>
                      ))}
                    </Box>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        )}
        {showResolved
          ? resolved.map((f) => (
              <Typography key={f.id} variant="body2" color="text.secondary">
                {[
                  f.status,
                  t(LABEL_KEY[f.type]),
                  nameOf(f.studentId),
                  f.details?.autoResolved
                    ? t("review.autoResolved", {
                        defaultValue: "auto-resolved",
                      })
                    : f.resolvedBy
                      ? t("review.byTeacher", { defaultValue: "by teacher" })
                      : "",
                  f.resolvedAt ? new Date(f.resolvedAt).toLocaleString() : "",
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </Typography>
            ))
          : null}
      </Box>
    </Box>
  );
};

export default FlagsPanel;
